import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";
import {
  businessToday,
  businessTodayRange,
  businessYesterdayRange,
  businessLastNDaysRange,
} from "src/utils/businessDay";
import { dedupeInFlight, staleGuard } from "src/utils/staleGuard";

// ─────────────────────────────────────────────────────────────
//  EstadisticasV2 · 2026-09-11
//
//  Slice del nuevo dashboard ejecutivo. Un solo thunk
//  `getDashboardV2` trae TODO (hero + heatmap + secciones) en
//  un único request. `getFiscalSummary` es aparte porque tiene
//  granularidad anual, no depende del rango del dashboard y
//  cambia rarísimo — cache 5 min en backend.
// ─────────────────────────────────────────────────────────────

const initialState = {
  loading: false,
  loadingFiscal: false,
  error: null,
  errorFiscal: null,
  dashboard: null,
  fiscal: null,
  // Rango activo. La UI lo persiste aquí para saber qué está
  // renderizando; los thunks aceptan también rangos ad-hoc.
  range: {
    desde: null,
    hasta: null,
    preset: "today",
  },
};

const slice = createSlice({
  name: "estadisticasV2",
  initialState,
  reducers: {
    startLoading(state) { state.loading = true; state.error = null; },
    hasError(state, action) { state.loading = false; state.error = action.payload || "Error"; },
    setDashboard(state, action) {
      state.loading = false;
      state.dashboard = action.payload;
    },
    startLoadingFiscal(state) { state.loadingFiscal = true; state.errorFiscal = null; },
    hasErrorFiscal(state, action) { state.loadingFiscal = false; state.errorFiscal = action.payload; },
    setFiscal(state, action) {
      state.loadingFiscal = false;
      state.fiscal = action.payload;
    },
    setRange(state, action) {
      state.range = { ...state.range, ...(action.payload || {}) };
    },
    clear(state) {
      state.dashboard = null;
      state.fiscal = null;
      state.error = null;
      state.errorFiscal = null;
    },
  },
});

export default slice.reducer;
export const { setRange, clear } = slice.actions;

// yyyy-MM-dd normalizado a UTC — coincide con lo que espera el backend.
function fmt(d) {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Trae el dashboard V2 completo para el rango indicado.
 * `force:true` evita el cache local + el gate staleGuard.
 */
export function getDashboardV2({ desde, hasta, force = false } = {}) {
  return async (dispatch) => {
    const from = fmt(desde);
    const to = fmt(hasta);
    if (!from || !to) return;

    const key = `analyticsV2/dashboard/${from}/${to}`;
    if (!force && staleGuard(key, 60_000)) return; // 60s eco del cache backend

    dispatch(slice.actions.startLoading());
    try {
      const data = await dedupeInFlight(key, async () => {
        const res = await axios.get(`/analytics/dashboard-v2`, {
          params: { desde: from, hasta: to },
        });
        return res.data;
      });
      dispatch(slice.actions.setDashboard(data));
      dispatch(slice.actions.setRange({ desde: from, hasta: to }));
    } catch (e) {
      dispatch(slice.actions.hasError(e?.response?.data?.message || e?.message || "Error"));
    }
  };
}

/**
 * Fiscal summary del año o de un rango custom.
 * - Modo año (por defecto): `getFiscalSummary(2026)` → 4 trimestres + anual.
 * - Modo rango: `getFiscalSummary(2026, { desde, hasta })` → solo resumen
 *   del rango (el backend rellena `Anual` con el total del rango y deja
 *   `Trimestres` vacío + marca `IsCustomRange=true`).
 *
 * Cache 5min en backend, y también gate por (año, rango) en el front.
 */
export function getFiscalSummary(year, { force = false, desde = null, hasta = null } = {}) {
  return async (dispatch) => {
    if (!year && !(desde && hasta)) return;
    const from = fmt(desde);
    const to = fmt(hasta);
    const isRange = !!(from && to);
    const key = isRange
      ? `analyticsV2/fiscal/custom/${from}/${to}`
      : `analyticsV2/fiscal/${year}`;
    if (!force && staleGuard(key, 5 * 60_000)) return;

    dispatch(slice.actions.startLoadingFiscal());
    try {
      const data = await dedupeInFlight(key, async () => {
        const params = { year: year || new Date().getFullYear() };
        if (isRange) {
          params.desde = from;
          params.hasta = to;
        }
        const res = await axios.get(`/analytics/fiscal-summary`, { params });
        return res.data;
      });
      dispatch(slice.actions.setFiscal(data));
    } catch (e) {
      dispatch(slice.actions.hasErrorFiscal(e?.response?.data?.message || e?.message || "Error"));
    }
  };
}

/**
 * Rango presets (calcula desde/hasta) para el selector del header.
 * Van en DÍAS DE NEGOCIO: «hoy» empieza a la hora de inicio de jornada
 * (`user.arqueoCutoffHour`, por defecto 04:00); antes de esa hora «hoy» es
 * todavía ayer. El backend recibe fechas yyyy-MM-dd y aplica la hora.
 * `h` = hora de inicio de jornada; `now` solo para tests.
 */
export const RANGE_PRESETS = {
  today: (h, now) => ({ ...businessTodayRange(h, now), label: "Hoy" }),
  yesterday: (h, now) => ({ ...businessYesterdayRange(h, now), label: "Ayer" }),
  week: (h, now) => ({ ...businessLastNDaysRange(7, h, now), label: "Últimos 7 días" }),
  month: (h, now) => ({ ...businessLastNDaysRange(30, h, now), label: "Últimos 30 días" }),
  quarter: (h, now) => ({ ...businessLastNDaysRange(90, h, now), label: "Últimos 90 días" }),
  year: (h, now) => {
    const hasta = businessToday(now ?? new Date(), h);
    const desde = new Date(hasta.getFullYear() - 1, hasta.getMonth(), hasta.getDate());
    return { desde, hasta, label: "Último año" };
  },
};
